import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import {
  Calendar, CalendarClock, Users,
  CheckCircle2, XCircle, Loader2,
  Pencil, Save, X, Shield, ShieldCheck, Search, ListChecks,
  ClipboardList, Link2, CalendarCheck, Activity,
} from 'lucide-react';
import { DataTable, type Column } from '../components/DataTable';
import { Dropdown } from '../components/Dropdown';
import { Modal } from '../components/Modal';
import { useToastStore } from "../../stores/toastStore";
import { RBACButton, RBACAction } from '../components/RBACButton';
import { PageShell } from '../components/layout/PageShell';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  getAssessmentPlan, updateAssessmentPlanStatus,
  triggerAssessmentPlan,
  ApiAssessmentPlan, type AssessmentPlanStatus,
  type ApiReviewedControl,
} from '../../api/frameworksApi';

/* ── Helpers ── */


const PLAN_STEPS = ['draft', 'in_review', 'approved'] as const;
const PLAN_STEP_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
};

function PlanStatusBadge({ status }: { status?: AssessmentPlanStatus }) {
  const current = status ?? 'draft';
  const activeIdx = PLAN_STEPS.indexOf(current as typeof PLAN_STEPS[number]);

  if (current === 'rejected') {
    return (
      <div className="ml-3 flex items-center gap-1 shrink-0 whitespace-nowrap">
        {PLAN_STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex items-center gap-1 opacity-40">
              <div className="size-2 rounded-full bg-muted-foreground/50" />
              <span className="text-[10px] font-medium">{PLAN_STEP_LABELS[s]}</span>
            </div>
            {i < PLAN_STEPS.length - 1 && <div className="w-4 h-px bg-muted-foreground/15" />}
          </React.Fragment>
        ))}
        <div className="w-4 h-px bg-muted-foreground/15" />
        <div className="flex items-center gap-1">
          <div className="size-2 rounded-full bg-red-500" />
          <span className="text-[10px] font-bold text-red-400">Rejected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-3 flex items-center gap-1 shrink-0 whitespace-nowrap">
      {PLAN_STEPS.map((s, i) => {
        const isCurrent = s === current;
        const isPast = activeIdx >= 0 && i < activeIdx;
        return (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1 ${isCurrent ? 'text-foreground' : isPast ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/60 dark:text-white/40'}`}>
              <div className={`size-2 rounded-full ${isCurrent ? 'bg-emerald-500 ring-2 ring-emerald-500/30' : isPast ? 'bg-emerald-500' : 'bg-muted-foreground/30 dark:bg-white/20'}`} />
              <span className={`text-[10px] ${isCurrent ? 'font-bold' : 'font-medium'}`}>
                {PLAN_STEP_LABELS[s]}
              </span>
            </div>
            {i < PLAN_STEPS.length - 1 && (
              <div className={`w-4 h-px ${isPast ? 'bg-emerald-500/50' : 'bg-muted-foreground/20 dark:bg-white/15'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Section card wrapper ── */
function SectionCard({ title, icon, children, count }: { title: string; icon: React.ReactNode; children: React.ReactNode; count?: number }) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320]">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06]">
        <span className="text-indigo-600 dark:text-indigo-400">{icon}</span>
        <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">{title}</h3>
        {count !== undefined && (
          <span className="ml-auto text-xs font-medium text-[#64748b] dark:text-white/50">{count} item{count !== 1 ? 's' : ''}</span>
        )}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

/* ── Detail field row ── */
function DetailField({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#0f172a] dark:text-white/70 mb-1">{label}</p>
      <p className={`text-[13px] font-medium text-[#475569] dark:text-white/60 ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-[#64748b] dark:text-white/50">-</span>}
      </p>
    </div>
  );
}

/* ── Select-all checkbox with indeterminate support ── */
function SelectAllCheckbox({ checked, indeterminate, onChange }: { checked: boolean; indeterminate: boolean; onChange: () => void }) {
  return (
    <input
      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-3.5 h-3.5 rounded border-[#cbd5e1] dark:border-white/20 accent-indigo-600 cursor-pointer"
    />
  );
}

/* ── Helper: resolve plan status from either field ── */
function resolvePlanStatus(plan: ApiAssessmentPlan): AssessmentPlanStatus {
  if (plan.status) return plan.status;
  if (plan.lifecycleStatus) return plan.lifecycleStatus as AssessmentPlanStatus;
  return 'draft';
}

/* ── Main Component ── */

export function AssessmentPlanDetail() {
  const { id } = useParams<{ id: string }>();

  const [plan, setPlan] = useState<ApiAssessmentPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState<'approved' | 'rejected' | null>(null);



  /* Success dialog */

  /* Edit mode (draft only) */
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');

  const [controlPage, setControlPage] = useState(0);
  const [controlPageSize, setControlPageSize] = useState(10);
  const [controlSearch, setControlSearch] = useState('');
  const [controlMethodFilter, setControlMethodFilter] = useState<string>('all');
  const [controlSortKey, setControlSortKey] = useState<'controlId' | 'title' | 'statement' | 'method' | 'status' | null>(null);
  const [controlSortDir, setControlSortDir] = useState<'asc' | 'desc'>('asc');

  const [selectedControlIds, setSelectedControlIds] = useState<Set<string>>(new Set());
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [modalMethod, setModalMethod] = useState('');

  const handleControlSort = (key: 'controlId' | 'title' | 'statement' | 'method' | 'status') => {
    if (controlSortKey === key) {
      setControlSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setControlSortKey(key);
      setControlSortDir('asc');
    }
    setControlPage(0);
  };

  /* Reviewed controls (fetched separately) */
  const [reviewedControls, setReviewedControls] = useState<ApiReviewedControl[]>([]);



  const fetchPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const planRes = await getAssessmentPlan(id!);
      const planData = planRes.data;
      setPlan(planData);
      setReviewedControls(planData.reviewedControls ?? []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load assessment plan');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  useEffect(() => { if (error) useToastStore.getState().showError("Error", error); }, [error]);


  const triggerEvaluationInBackground = (planId: string) => {
    const ac = new AbortController();
    const timeoutId = window.setTimeout(() => ac.abort(), 90_000);
    triggerAssessmentPlan(planId, { esIndex: 'linux-data-on-prem', signal: ac.signal })
      .then(() => {
        fetchPlan();
      })
      .catch((err: any) => {
        if (err?.name === 'AbortError') {
          useToastStore.getState().showError(
            "Evaluator timeout",
            "Evaluator service did not respond in time. Try again or contact support.",
          );
        } else {
          const apiMsg = err?.responseBody?.errors?.[0]?.detail
            || err?.responseBody?.errors?.[0]?.title
            || err?.message;
          useToastStore.getState().showError("Evaluation failed", apiMsg || "Failed to trigger assessment.");
        }
      })
      .finally(() => window.clearTimeout(timeoutId));
  };

  const handleStatusUpdate = async (newStatus: AssessmentPlanStatus) => {
    if (!plan) return;
    const isApproval = newStatus === 'approved';
    const comments = isApproval
      ? 'Plan reviewed and approved by ISSO'
      : newStatus === 'rejected'
        ? 'Plan reviewed and rejected by ISSO'
        : '';
    setStatusUpdating(newStatus as 'approved' | 'rejected');
    try {
      const res = await updateAssessmentPlanStatus(plan.id, newStatus, comments);
      setPlan(res.data);
      if (isApproval) {
        useToastStore.getState().showSuccess(
          "Approved",
          "Assessment plan approved.",
        );
        // triggerEvaluationInBackground(plan.id);
      } else {
        useToastStore.getState().showSuccess("Success", "Assessment plan has been rejected.");
      }
    } catch (err: any) {
      const body = err?.responseBody;
      const apiMsg = body?.errors?.[0]?.detail || body?.errors?.[0]?.title || err?.message;
      useToastStore.getState().showError("Error", apiMsg || `Failed to ${isApproval ? 'approve' : 'reject'} assessment plan.`);
    } finally {
      setStatusUpdating(null);
    }
  };

  const handleEditStart = () => {
    if (!plan) return;
    setEditTitle(plan.title ?? '');
    setEditing(true);
  };

  const handleEditSave = async () => {
    if (!plan) return;
    setPlan(prev => prev ? { ...prev, title: editTitle } : prev);
    setEditing(false);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditTitle('');
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <PageShell className="flex items-center justify-center gap-3 py-24">
        <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
        <span className="text-[13px] text-muted-foreground">Loading</span>
      </PageShell>
    );
  }

  /* ── Error state ── */
  if (error || !plan) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-sm text-muted-foreground">Something went wrong.</p>
      </div>
    );
  }

  const displayTitle = plan.title ?? `Assessment Plan ${plan.id.slice(-8)}`;

  const planStatus = resolvePlanStatus(plan);
  const isDraft = planStatus === 'draft';

  const sspTitle = plan.sspTitle ?? plan.importSspHref?.split('/').pop() ?? null;

  return (
    <PageShell className="space-y-6">
      <Breadcrumb
        parent="Assessment Plans"
        parentTo="/assessment-plans"
        current={editing ? "" : displayTitle}
        badge={<PlanStatusBadge status={planStatus} />}
        actions={(editing || isDraft || planStatus === 'in_review') ? (
          <>
            {editing && (
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="text-sm font-medium text-[#0f172a] dark:text-white bg-transparent border-b-2 border-indigo-500 dark:border-indigo-500 outline-none px-1 py-0.5 min-w-[200px]"
                autoFocus
              />
            )}
            {isDraft && !editing && (
              <button
                onClick={handleEditStart}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.1] text-xs font-medium text-[#475569] dark:text-white/60 hover:bg-[#f8fafc] dark:hover:bg-white/[0.04] transition-colors"
              >
                <Pencil className="size-3.5" />
                Edit
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={handleEditCancel}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/[0.1] text-xs font-medium text-[#475569] dark:text-white/60 hover:bg-[#f8fafc] dark:hover:bg-white/[0.04] transition-colors"
                >
                  <X className="size-3.5" />
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Save className="size-3.5" />
                  Save
                </button>
              </>
            )}
            {(planStatus === 'in_review' || planStatus === 'draft') && !editing && (
              <RBACAction module="assessment_plan" action="approve">
                <>
                  <button
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={!!statusUpdating}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 dark:bg-emerald-500 text-white text-[14px] font-bold shadow-md shadow-emerald-500/25 hover:bg-emerald-600 dark:hover:bg-emerald-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {statusUpdating === 'approved' ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="size-4" />
                    )}
                    Approve
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('rejected')}
                    disabled={!!statusUpdating}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-500 dark:bg-red-500 text-white text-[14px] font-bold shadow-md shadow-red-500/25 hover:bg-red-600 dark:hover:bg-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {statusUpdating === 'rejected' ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <XCircle className="size-4" />
                    )}
                    Reject
                  </button>
                </>
              </RBACAction>
            )}
          </>
        ) : undefined}
      />

      {/* ── Plan Information ── */}
      <SectionCard title="Plan Information" icon={<Shield className="size-4" />}>
        {(() => {
          const startDate = plan.task?.startDate ? new Date(plan.task.startDate).toLocaleDateString('en-GB') : '-';
          const endDate = plan.task?.endDate ? new Date(plan.task.endDate).toLocaleDateString('en-GB') : '-';
          const cards: Array<{
            label: string;
            value: React.ReactNode;
            valueText: string;
            icon: typeof ClipboardList;
            color: string;
            bg: string;
          }> = [
            { label: 'Plan Name', value: displayTitle, valueText: displayTitle, icon: ClipboardList, color: 'text-indigo-500 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-500/10' },
            { label: 'Linked SSP', value: sspTitle ?? plan.importSspHref ?? '-', valueText: sspTitle ?? plan.importSspHref ?? '-', icon: Link2, color: 'text-sky-500 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-500/10' },
            {
              label: 'Status',
              value: (() => {
                const s = planStatus ?? 'draft';
                const cfg: Record<string, { label: string; cls: string }> = {
                  draft:      { label: 'Draft',      cls: 'bg-slate-100 dark:bg-white/[0.06] text-slate-600 dark:text-white/60 border-slate-200 dark:border-white/10' },
                  in_review:  { label: 'In Review',  cls: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/20' },
                  approved:   { label: 'Approved',   cls: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' },
                  rejected:   { label: 'Rejected',   cls: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20' },
                };
                const { label, cls } = cfg[s] ?? cfg['draft'];
                return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>{label}</span>;
              })(),
              valueText: planStatus ?? '',
              icon: Activity,
              color: 'text-violet-500 dark:text-violet-400',
              bg: 'bg-violet-50 dark:bg-violet-500/10',
            },
            { label: 'Start Date', value: startDate, valueText: startDate, icon: Calendar, color: 'text-emerald-500 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
            { label: 'End Date', value: endDate, valueText: endDate, icon: CalendarCheck, color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10' },
          ];
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
              {cards.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.label}
                    className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-4 py-3 flex items-center gap-3 min-w-0"
                  >
                    <div className={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                      <Icon className={`size-4 ${c.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-medium text-[#94a3b8] dark:text-white/60">
                        {c.label}
                      </p>
                      <div className="text-[13px] font-semibold text-[#0f172a] dark:text-white truncate" title={c.valueText}>
                        {c.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </SectionCard>

      {/* ── Reviewed Controls (from API) ── */}
      <SectionCard title="Selected Controls" icon={<Shield className="size-4" />} count={reviewedControls.length}>
        {(() => {
          type StmtType = ApiReviewedControl['statements'][number];
          type ControlRow = { ctrl: ApiReviewedControl; stmt: StmtType | null };
          const allRows: ControlRow[] = reviewedControls.flatMap((ctrl): ControlRow[] =>
            ctrl.statements.length > 0
              ? ctrl.statements.map(stmt => ({ ctrl, stmt }))
              : [{ ctrl, stmt: null }]
          );

          // Filter
          const filteredRows = allRows.filter(row => {
            const q = controlSearch.toLowerCase();
            const matchesSearch = !q ||
              row.ctrl.controlId.toLowerCase().includes(q) ||
              (row.ctrl.controlDescription ?? '').toLowerCase().includes(q) ||
              (row.stmt?.statementDescription ?? '').toLowerCase().includes(q);
            const matchesMethod = controlMethodFilter === 'all' ||
              (row.stmt?.assessmentMethods ?? []).includes(controlMethodFilter);
            return matchesSearch && matchesMethod;
          });

          // Sort
          const sortedRows = [...filteredRows].sort((a, b) => {
            if (!controlSortKey) return 0;
            const aVal = controlSortKey === 'controlId' ? a.ctrl.controlId
              : controlSortKey === 'title' ? (a.ctrl.controlDescription ?? a.ctrl.controlId)
              : controlSortKey === 'method' ? (a.stmt?.assessmentMethods?.[0] ?? '')
              : controlSortKey === 'status' ? (a.ctrl.implementationStatus ?? '')
              : (a.stmt?.statementDescription ?? '');
            const bVal = controlSortKey === 'controlId' ? b.ctrl.controlId
              : controlSortKey === 'title' ? (b.ctrl.controlDescription ?? b.ctrl.controlId)
              : controlSortKey === 'method' ? (b.stmt?.assessmentMethods?.[0] ?? '')
              : controlSortKey === 'status' ? (b.ctrl.implementationStatus ?? '')
              : (b.stmt?.statementDescription ?? '');
            const cmp = aVal.localeCompare(bVal);
            return controlSortDir === 'asc' ? cmp : -cmp;
          });

          const pagedRows = sortedRows.slice(controlPage * controlPageSize, (controlPage + 1) * controlPageSize);

          // Selection helpers
          const pageControlIds = [...new Set(pagedRows.map(r => r.ctrl.controlId))];
          const allPageSelected = pageControlIds.length > 0 && pageControlIds.every(id => selectedControlIds.has(id));
          const somePageSelected = pageControlIds.some(id => selectedControlIds.has(id)) && !allPageSelected;

          const toggleRow = (controlId: string) => {
            setSelectedControlIds(prev => {
              const next = new Set(prev);
              if (next.has(controlId)) next.delete(controlId);
              else next.add(controlId);
              return next;
            });
          };

          const toggleAll = () => {
            setSelectedControlIds(prev => {
              const next = new Set(prev);
              if (allPageSelected) pageControlIds.forEach(id => next.delete(id));
              else pageControlIds.forEach(id => next.add(id));
              return next;
            });
          };

          const SortIcon = ({ col }: { col: string }) => {
            const active = controlSortKey === col;
            return (
              <span className="inline-flex flex-col items-center ml-2 select-none" style={{ fontSize: '7px', lineHeight: '7px' }}>
                <span className={`font-black ${active && controlSortDir === 'asc' ? 'text-[#0f172a] dark:text-white' : 'text-[#94a3b8] dark:text-white/40'}`}>▲</span>
                <span className={`font-black ${active && controlSortDir === 'desc' ? 'text-[#0f172a] dark:text-white' : 'text-[#94a3b8] dark:text-white/40'}`}>▼</span>
              </span>
            );
          };

          const columns: Column<ControlRow>[] = [
            {
              key: 'no',
              header: <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">#</span>,
              align: 'center',
              render: (_row, idx) => (
                <span className="text-[#475569] dark:text-white/50 font-medium text-[12px]">{controlPage * controlPageSize + idx + 1}</span>
              ),
            },
            {
              key: 'control',
              header: (
                <button onClick={() => handleControlSort('controlId')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                  Control Id <SortIcon col="controlId" />
                </button>
              ),
              align: 'center',
              render: (row) => (
                <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                  {row.ctrl.controlId?.toUpperCase()}
                </span>
              ),
            },
            {
              key: 'title',
              header: (
                <button onClick={() => handleControlSort('title')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                  Title <SortIcon col="title" />
                </button>
              ),
              align: 'left', headerClassName: '!text-center',
              ellipsis: 2,
              tooltip: (row) => row.ctrl.controlDescription || row.ctrl.controlId || '',
              render: (row) => (
                <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">
                  {row.ctrl.controlDescription || row.ctrl.controlId?.toUpperCase()}
                </p>
              ),
            },
            {
              key: 'statement',
              header: (
                <button onClick={() => handleControlSort('statement')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                  Statement <SortIcon col="statement" />
                </button>
              ),
              align: 'left', headerClassName: '!text-center',
              ellipsis: 2,
              tooltip: (row) => row.stmt?.statementDescription || '',
              render: (row) => row.stmt ? (
                <p className="font-medium text-[#0f172a] dark:text-white/80 text-[13px] break-words">{row.stmt.statementDescription}</p>
              ) : (
                <span className="text-[#94a3b8] dark:text-white/30">-</span>
              ),
            },
            // Component column hidden for now per plan detail UI update.
            // {
            //   key: 'component',
            //   header: (
            //     <button onClick={() => handleControlSort('status')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
            //       Component <SortIcon col="component" />
            //     </button>
            //   ),
            //   align: 'center',
            //   render: (row) => {
            //     const name = row.ctrl.componentName;
            //     const comps = row.ctrl.byComponents ?? [];
            //     const displayName = name || comps[0]?.componentName;
            //     if (!displayName) return <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">-</span>;
            //     return (
            //       <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80 truncate block max-w-full cursor-default" title={displayName}>
            //         {displayName}
            //         {comps.length > 1 && <span className="text-[10px] text-[#94a3b8] dark:text-white/40 ml-1">+{comps.length - 1}</span>}
            //       </span>
            //     );
            //   },
            // },
            {
              key: 'status',
              header: (
                <button onClick={() => handleControlSort('status')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                  Status <SortIcon col="status" />
                </button>
              ),
              align: 'center',
              render: (row) => {
                const s = row.ctrl.implementationStatus?.toLowerCase();
                const statusStyles: Record<string, string> = {
                  implemented:       'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-200 dark:ring-emerald-500/20',
                  partial:           'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20',
                  planned:           'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-200 dark:ring-blue-500/20',
                  'not-implemented': 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 ring-red-200 dark:ring-red-500/20',
                };
                const statusLabels: Record<string, string> = {
                  implemented: 'Implemented', partial: 'Partial',
                  planned: 'Planned', 'not-implemented': 'Not Implemented',
                };
                if (!s || !statusStyles[s]) return <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">-</span>;
                return (
                  <span className={`inline-flex items-center justify-center w-full max-w-[150px] px-2 py-1 rounded-full text-xs font-bold ring-1 whitespace-nowrap ${statusStyles[s]}`}>
                    {statusLabels[s]}
                  </span>
                );
              },
            },
            {
              key: 'assessmentMethod',
              header: (
                <button onClick={() => handleControlSort('method')} className="flex items-center gap-1 mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors whitespace-nowrap">
                  Assessment Method <SortIcon col="method" />
                </button>
              ),
              align: 'center',
              render: (row) => {
                const methods = row.stmt?.assessmentMethods ?? [];
                if (methods.length === 0) return <span className="text-[#94a3b8] dark:text-white/30 text-[11px]">-</span>;
                const methodStyles: Record<string, string> = {
                  interview: 'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 ring-sky-200 dark:ring-sky-500/20',
                  examine:   'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-indigo-200 dark:ring-indigo-500/20',
                  test:      'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-500/20',
                };
                return (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {methods.map(m => (
                      <span key={m} className={`inline-flex items-center justify-center w-[90px] px-2 py-1 rounded-full text-[11px] font-bold ring-1 capitalize ${methodStyles[m.toLowerCase()] ?? methodStyles.test}`}>
                        {m}
                      </span>
                    ))}
                  </div>
                );
              },
            },
          ];

          return (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[180px] sm:max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search controls"
                    value={controlSearch}
                    onChange={e => { setControlSearch(e.target.value); setControlPage(0); }}
                    className="w-full h-[34px] pl-8 pr-3 py-1.5 text-[13px] bg-[#f8fafc] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/40"
                  />
                </div>
                <Dropdown
                  size="sm"
                  variant="pill"
                  className="min-w-[120px]"
                  value={controlMethodFilter}
                  onChange={(v) => { setControlMethodFilter(v); setControlPage(0); }}
                  options={[
                    { value: "all", label: "All Status" },
                    { value: "interview", label: "Interview" },
                    { value: "examine", label: "Examine" },
                    { value: "test", label: "Test" },
                  ]}
                />

                {/* Select Assessment Method — only visible when controls are checked */}
                {selectedControlIds.size > 0 && (
                  <button
                    onClick={() => { setModalMethod(''); setShowMethodModal(true); }}
                    className="flex items-center gap-2 h-[34px] px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-semibold transition-all shadow-sm shadow-indigo-500/20"
                  >
                    <ListChecks className="size-3.5" />
                    Select Assessment Method
                    <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/20 text-[11px] font-bold leading-none">
                      {selectedControlIds.size}
                    </span>
                  </button>
                )}
                <div className="flex items-center gap-2 w-full md:w-auto md:ml-auto flex-shrink-0">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">
                    Rows per page
                  </label>
                  <Dropdown
                    value={String(controlPageSize)}
                    onChange={(v) => {
                      setControlPageSize(Number(v));
                      setControlPage(0);
                    }}
                    options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                    size="sm"
                    align="end"
                    className="!min-w-[80px]"
                  />
                </div>
              </div>

              <DataTable<ControlRow>
                columns={columns}
                data={pagedRows}
                emptyText={reviewedControls.length === 0 ? 'No controls assigned to this plan' : 'No controls match your search'}
                rowKey={(row, idx) => `${row.ctrl.id}-${row.stmt?.id ?? idx}`}
                page={controlPage}
                pageSize={controlPageSize}
                totalItems={sortedRows.length}
                onPageChange={setControlPage}
                itemLabel="controls"
              />
            </>
          );
        })()}
      </SectionCard>

      {/* ── System Owners + Schedule Tasks (side by side) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Owners (left) */}
        {(() => {
          const assignees = plan.task?.assignees ?? [];
          return (
            <SectionCard title="System Owners" icon={<Users className="size-4" />} count={assignees.length}>
              {assignees.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No system owners assigned</p>
              ) : (
                <div className="space-y-3">
                  {assignees.map(owner => (
                    <div key={owner.userId} className="flex items-center gap-3 rounded-lg border border-[#f1f5f9] dark:border-white/[0.04] bg-[#f8fafc] dark:bg-white/[0.02] p-3">
                      <div className="size-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center text-xs font-bold text-indigo-700 dark:text-indigo-300 flex-shrink-0">
                        {owner.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#0f172a] dark:text-white/90 truncate">{owner.displayName}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{owner.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          );
        })()}

        {/* Schedule Tasks (right) */}
        <SectionCard title="Schedule Tasks" icon={<CalendarClock className="size-4" />} count={plan.task ? 1 : 0}>
          {!plan.task ? (
            <p className="text-sm text-muted-foreground text-center py-4">No tasks scheduled</p>
          ) : (
            <div className="space-y-0">
              <div className="rounded-lg border border-[#f1f5f9] dark:border-white/[0.04] bg-[#f8fafc] dark:bg-white/[0.02] p-3">
                <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="size-3.5 text-[#475569] dark:text-white/50" />
                    <span className="font-medium text-[#475569] dark:text-white/60">Start Date:</span>
                    <span className="text-[#0f172a] dark:text-white/80 font-medium">{plan.task.startDate ? new Date(plan.task.startDate).toLocaleDateString('en-GB') : '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="size-3.5 text-[#475569] dark:text-white/50" />
                    <span className="font-medium text-[#475569] dark:text-white/60">End Date:</span>
                    <span className="text-[#0f172a] dark:text-white/80 font-medium">{plan.task.endDate ? new Date(plan.task.endDate).toLocaleDateString('en-GB') : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <Modal
        open={showMethodModal}
        onClose={() => setShowMethodModal(false)}
        title="Assign Assessment Method"
        description={`Applying to ${selectedControlIds.size} selected control${selectedControlIds.size !== 1 ? 's' : ''}`}
        footer={
          <>
            <button
              onClick={() => setShowMethodModal(false)}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!modalMethod) return;
                useToastStore.getState().showSuccess("Success", `"${modalMethod}" assigned to ${selectedControlIds.size} control${selectedControlIds.size !== 1 ? 's' : ''}`);
                setShowMethodModal(false);
                setSelectedControlIds(new Set());
                setModalMethod('');
              }}
              disabled={!modalMethod}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-indigo-500/20"
            >
              <ListChecks className="size-3.5" />
              Apply to {selectedControlIds.size} Control{selectedControlIds.size !== 1 ? 's' : ''}
            </button>
          </>
        }
      >
        <div>
          <p className="text-[11px] font-semibold text-[#94a3b8] dark:text-white/40 tracking-wide mb-3">Selected Controls</p>
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {[...selectedControlIds].map(ctrlId => {
              const ctrl = reviewedControls.find(c => c.controlId === ctrlId);
              return (
                <div key={ctrlId} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.04]">
                  <span className="text-[11px] font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded flex-shrink-0">
                    {ctrlId?.toUpperCase()}
                  </span>
                  <span className="text-[12px] text-[#475569] dark:text-white/60 truncate">
                    {ctrl?.controlDescription || ctrlId?.toUpperCase()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold text-[#94a3b8] dark:text-white/40 tracking-wide mb-3">Assessment Method</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {([
              { value: 'interview', label: 'Interview', color: 'sky'     },
              { value: 'examine',   label: 'Examine',   color: 'indigo'  },
              { value: 'test',      label: 'Test',      color: 'emerald' },
            ] as const).map(({ value, label, color }) => {
              const isSelected = modalMethod === value;
              const colorMap = {
                sky:     { border: 'border-sky-500',     bg: 'bg-sky-50 dark:bg-sky-500/10',       dot: 'bg-sky-500',     text: 'text-sky-700 dark:text-sky-400'      },
                indigo:  { border: 'border-indigo-500',  bg: 'bg-indigo-50 dark:bg-indigo-500/10', dot: 'bg-indigo-500',  text: 'text-indigo-700 dark:text-indigo-400' },
                emerald: { border: 'border-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-500/10', dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-400' },
              };
              const c = colorMap[color];
              return (
                <button
                  key={value}
                  onClick={() => setModalMethod(value)}
                  className={`flex items-center gap-2 px-4 py-3.5 rounded-xl border-2 transition-all ${
                    isSelected
                      ? `${c.border} ${c.bg}`
                      : 'border-[#e2e8f0] dark:border-white/[0.06] hover:border-[#c7d2de] dark:hover:border-white/[0.12] bg-white dark:bg-transparent'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? c.dot : 'bg-[#cbd5e1] dark:bg-white/20'}`} />
                  <span className={`text-[13px] font-semibold ${isSelected ? c.text : 'text-[#475569] dark:text-white/60'}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

    </PageShell>
  );
}
